'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { type AuthChangeEvent, type Session, type User } from '@supabase/supabase-js';
import * as Sentry from '@sentry/nextjs';
import { getSupabaseBrowserClient } from '@/lib/supabase/client';
import { getImplicitFlowClient } from '@/lib/supabase/implicit-client';
import { buildTracingHeaders, generateTraceContext } from '@/lib/tracing';
import { useRouter } from 'next/navigation';
import { trackEvent, identifyUser, resetAnalytics } from '@/lib/posthog';
import { AuthEvents } from '@/lib/analytics-events';
import { clearPendingRequests, deduplicatedFetch } from '@/lib/request-deduplicator';
import {
  resetSessionExpiredGuard,
  broadcastSignOut,
  closeAuthChannel,
} from '@/lib/session-refresh';
import { clearAllPersistedStores } from '@/stores/persist-registry';
import { useAuthProfileStore, type UserProfile } from '@/stores/auth-profile-store';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { consumePostAuthRedirect, clearPostAuthRedirect } from '@/lib/auth/post-auth-redirect';
import type { Workspace } from '@/api/endpoints/workspaces';
import { logWarn } from '@/lib/client-logger';

/**
 * Resolve the user's dashboard URL using workspace-scoped routing.
 *
 * When `serverWorkspaceId` is provided (from a fresh /auth/sync or
 * /onboarding/status response), it takes absolute priority — this
 * eliminates the race condition where Zustand store rehydration or
 * stale cookies redirect to the wrong workspace after login.
 *
 * Fallback priority (only when no server override):
 * 1. The auth-profile store (just populated by fetchProfile)
 * 2. The workspace store (persisted from a previous session)
 * 3. The virza-ws-id cookie
 * Falls back to bare /home (middleware will redirect).
 */
/** Mark auth transition for overlay (called from async auth callbacks, not during render). */
function markAuthTransition() {
  try {
    window.sessionStorage.setItem('virza-auth-transition', 'true');
  } catch {
    // SSR or private browsing — safe to ignore
  }
}

function resolveDashboardUrl(serverWorkspaceId?: string): string {
  const wsId =
    serverWorkspaceId ??
    useAuthProfileStore.getState().profile?.active_workspace_id ??
    useWorkspaceStore.getState().currentWorkspace?.id ??
    // eslint-disable-next-line no-restricted-syntax -- inside function body, not module scope; SSR guard is correct
    (typeof document !== 'undefined'
      ? document.cookie
          .split('; ')
          .find((c) => c.startsWith('virza-ws-id='))
          ?.split('=')[1]
      : undefined);
  return wsId ? `/w/${wsId}/home` : '/home';
}

/** Read and clear the invite token cookie set by the invite accept page. */
function consumeInviteTokenCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.split('; ').find((c) => c.startsWith('virza-invite-token='));
  if (!match) return null;
  const token = decodeURIComponent(match.split('=')[1]);
  // Clear cookie immediately — single use
  document.cookie = 'virza-invite-token=; path=/; max-age=0';
  return token || null;
}

/**
 * Try to auto-accept a pending invitation after authentication.
 * Returns the workspace ID to redirect to, or null if no invite / accept failed.
 */
async function tryAutoAcceptInvite(): Promise<string | null> {
  const inviteToken = consumeInviteTokenCookie();
  if (!inviteToken) return null;

  try {
    const acceptRes = await fetch('/api/v1/invitations/accept', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...buildTracingHeaders(generateTraceContext()),
      },
      credentials: 'include',
      body: JSON.stringify({ token: inviteToken }),
    });
    if (!acceptRes.ok) return null;

    const invitation = await acceptRes.json();
    const wsId = invitation.workspaceId ?? invitation.workspace_id;
    const wsName = invitation.workspaceName ?? invitation.workspace_name;

    // Set accepted workspace as current so sidebar/layout are ready.
    // Uses a minimal Workspace shape — the layout will hydrate full data
    // from the workspace list query on mount.
    useWorkspaceStore.getState().setCurrentWorkspace({
      id: wsId,
      name: wsName,
      type: 'team',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      ownerId: '',
    } as Workspace);

    return wsId;
  } catch {
    return null;
  }
}

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

/**
 * Register a new user account on the backend after Supabase signup + OTP verification.
 * Calls POST /auth/register — the dedicated registration endpoint.
 * Retries up to 3 times with exponential backoff — account creation is critical.
 *
 * Uses cookie-based auth (credentials: "include") so the proxy injects the
 * Bearer token server-side — no raw JWT exposure to client JS.
 *
 * Note: No Turnstile token is sent here. Turnstile tokens are single-use and
 * already consumed by Supabase GoTrue during signUp(). This endpoint is
 * protected by Supabase JWT + OTP verification + IP rate limiting instead.
 */
async function registerAccountWithBackend(metadata: {
  first_name?: string;
  last_name?: string;
}): Promise<{ ok: boolean; status: number }> {
  const MAX_RETRIES = 3;

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    if (attempt > 0) {
      await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt - 1)));
    }

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...buildTracingHeaders(generateTraceContext()),
      };

      const res = await fetch('/api/v1/auth/register', {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(metadata),
      });

      // 201 = created, 200 = already exists (idempotent)
      if (res.ok) {
        return { ok: true, status: res.status };
      }

      // 409 = user already created by a concurrent /auth/sync — treat as success.
      // This happens when onAuthStateChange fires before verifyOtp completes.
      if (res.status === 409) {
        return { ok: true, status: res.status };
      }

      // Don't retry on other 4xx client errors
      if (res.status >= 400 && res.status < 500) {
        return { ok: false, status: res.status };
      }

      // Server error — retry
      continue;
    } catch {
      // Network error — retry
      continue;
    }
  }

  return { ok: false, status: 0 };
}

/**
 * Auth-form pathnames where authenticated users are not expected — the edge
 * middleware redirects them away before rendering, but there is a brief client
 * window during which the hook still mounts. We skip the profile fetch on
 * these routes to avoid noisy 401 responses against a possibly-stale token.
 */
const AUTH_PAGE_PATHS = new Set([
  '/login',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
]);

function isAuthPage(): boolean {
  // eslint-disable-next-line no-restricted-syntax -- SSR guard in plain utility function
  if (typeof window === 'undefined') return false;
  const { pathname } = window.location;
  return AUTH_PAGE_PATHS.has(pathname) || pathname.startsWith('/auth/');
}

interface AuthState {
  status: AuthStatus;
  user: User | null;
  session: Session | null;
}

// Module-level flag to suppress fetchProfile during OTP verification.
// When verifyOtp() is in progress it calls registerAccountWithBackend()
// which races with the onAuthStateChange SIGNED_IN → fetchProfile() sync.
// This flag prevents the listener from firing a concurrent /auth/sync.
let _registrationInProgress = false;

export function useAuth() {
  const supabase = getSupabaseBrowserClient();
  const router = useRouter();

  const [state, setState] = useState<AuthState>({
    status: 'loading',
    user: null,
    session: null,
  });

  // Profile lives in a shared Zustand store so ALL components that call
  // useAuth() see the same data — no more isolated per-instance useState.
  const profile = useAuthProfileStore((s) => s.profile);

  // onAuthStateChange is the single source of truth for auth state.
  //
  // Supabase fires INITIAL_SESSION to every new subscriber immediately with the
  // current session (or null), so there is no need for a separate getSession()
  // call or an authInitializedRef guard. This is also React Strict Mode-safe:
  // when Strict Mode unmounts → remounts the component, the second mount
  // registers a fresh subscriber and receives INITIAL_SESSION again, ensuring
  // status always resolves out of "loading".
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, session: Session | null) => {
      if (session?.user) {
        setState({ status: 'authenticated', user: session.user, session });
        Sentry.setUser({ id: session.user.id });

        // Fetch profile on initial load and explicit sign-in only.
        // TOKEN_REFRESHED / USER_UPDATED / PASSWORD_RECOVERY don't need a re-fetch.
        // Skip on auth-form pages — the session may be stale and the proxy will 401.
        // Skip when registration is in progress — verifyOtp() handles its own
        // register → fetchProfile sequence; a concurrent sync would race.
        if (
          (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') &&
          !isAuthPage() &&
          !_registrationInProgress
        ) {
          fetchProfile(session.user.id);
        }
      } else {
        setState({ status: 'unauthenticated', user: null, session: null });
        Sentry.setUser(null);
        if (event === 'SIGNED_OUT') {
          useAuthProfileStore.getState().clearProfile();
        }
      }
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // uid is passed directly by every caller — eliminates a sequential getSession()
  // call that previously fired before the parallel /auth/sync + /onboarding/status
  // requests, shaving one async round-trip off the app-startup critical path.
  async function fetchProfile(uid: string = 'unknown'): Promise<UserProfile | null> {
    // Deduplicate: If multiple components mount simultaneously and call this,
    // only execute the request once and share the result.
    return deduplicatedFetch(`profile-${uid}`, async () => {
      try {
        // Sequence: /auth/sync first (JIT-creates user if missing), then
        // /onboarding/status only after sync succeeds. The previous parallel
        // Promise.all caused a race where /onboarding/status arrived at the
        // backend before /auth/sync had created the user record, generating
        // spurious user.not_found + http.problem log noise.
        const profileTraceHeaders = buildTracingHeaders(generateTraceContext());

        const syncRes = await fetch('/api/v1/auth/sync', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...profileTraceHeaders },
          credentials: 'include',
        });

        if (syncRes.ok) {
          const profileData = await syncRes.json();

          // Enrich with onboarding state (only after sync confirms user exists).
          // The sync response already contains onboarding_completed, but
          // /onboarding/status provides the canonical active_workspace_id.
          try {
            const onboardingRes = await fetch('/api/v1/onboarding/status', {
              headers: { ...profileTraceHeaders },
              credentials: 'include',
            });
            if (onboardingRes.ok) {
              const onboardingData = await onboardingRes.json();
              // Only overwrite if the payload supplies a non-undefined value —
              // both fields are optional in OnboardingProfileResponse, so a
              // missing field must not clear the sync-derived value already in
              // profileData.
              const resolvedOnboardingCompleted =
                onboardingData.onboardingCompleted ?? onboardingData.onboarding_completed;
              if (resolvedOnboardingCompleted !== undefined) {
                profileData.onboarding_completed = resolvedOnboardingCompleted;
              }
              const resolvedActiveWorkspaceId =
                onboardingData.activeWorkspaceId ?? onboardingData.active_workspace_id;
              if (resolvedActiveWorkspaceId !== undefined) {
                profileData.active_workspace_id = resolvedActiveWorkspaceId;
              }
            }
          } catch {
            // Onboarding enrichment is best-effort — sync data is sufficient
          }

          useAuthProfileStore.getState().setProfile(profileData);
          // Enrich Sentry user context with workspace scope for error triage
          if (profileData.active_workspace_id) {
            Sentry.setUser({ id: uid, workspace_id: profileData.active_workspace_id });
          }
          return profileData;
        }

        // 503 = Supabase / auth service temporarily unavailable — log but don't
        // treat as fatal.  The middleware has already validated the session; we
        // just can't enrich the profile right now.
        if (syncRes.status === 503) {
          logWarn('hook.auth.profile_sync_unavailable', {
            status: 503,
            detail: 'Will retry on next navigation',
          });
        }

        return null;
      } catch (err) {
        // Profile fetch is best-effort — auth state is still valid without it.
        Sentry.captureException(err, { level: 'warning', tags: { flow: 'fetch_profile' } });
        return null;
      }
    });
  }

  const signInWithPassword = useCallback(
    async (email: string, password: string, captchaToken?: string) => {
      const start = Date.now();
      trackEvent(AuthEvents.SIGN_IN_ATTEMPTED, { method: 'password' });
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: captchaToken ? { captchaToken } : undefined,
      });
      if (error) {
        trackEvent(AuthEvents.SIGN_IN_FAILED, {
          method: 'password',
          error: error.message,
          duration_ms: Date.now() - start,
        });
        throw error;
      }

      trackEvent(AuthEvents.SIGN_IN_SUCCEEDED, {
        method: 'password',
        duration_ms: Date.now() - start,
      });

      // Check if user has MFA enrolled — if so, redirect to MFA challenge
      // instead of the dashboard. The session is AAL1 (password only) and
      // must be upgraded to AAL2 before granting full access.
      const { data: factorsData } = await supabase.auth.mfa.listFactors();
      const verifiedFactors =
        factorsData?.totp?.filter((f: { status: string }) => f.status === 'verified') ?? [];

      if (verifiedFactors.length > 0) {
        trackEvent(AuthEvents.MFA_CHALLENGE_REQUIRED);
        // Do NOT consume the stored post-auth redirect here — it survives the
        // /mfa-verify hop in sessionStorage and is consumed by MFAVerifyForm
        // on successful TOTP/recovery verification, so the user lands on their
        // intended deep link after MFA completes.
        router.push('/mfa-verify');
        return data;
      }

      // No MFA — proceed normally
      identifyUser(data.user.id, {
        email: data.user.email,
        name:
          [data.user.user_metadata?.first_name, data.user.user_metadata?.last_name]
            .filter(Boolean)
            .join(' ') || undefined,
        created_at: data.user.created_at,
      });
      resetSessionExpiredGuard();
      const freshProfile = await fetchProfile(data.user.id);

      // Auto-accept pending invite if user signed in from an invite link.
      // The accepted-workspace dashboard is the canonical destination for this
      // flow, so clear any stored post-auth redirect — it was intended for a
      // different context and must not leak into the post-invite navigation.
      const acceptedWsId = await tryAutoAcceptInvite();
      if (acceptedWsId) {
        clearPostAuthRedirect();
        markAuthTransition();
        router.push(`/w/${acceptedWsId}/home`);
        return data;
      }

      // Pass the server-authoritative workspace ID directly — eliminates
      // the race where Zustand rehydration or a stale cookie redirects to
      // the wrong workspace before the profile store is populated.
      markAuthTransition();

      // Respect a stored post-auth redirect (e.g. ?redirect= from login page).
      // This is consumed once — if nothing is stored, falls back to the
      // workspace-scoped dashboard URL.
      const postAuthRedirect = consumePostAuthRedirect();
      router.push(postAuthRedirect ?? resolveDashboardUrl(freshProfile?.active_workspace_id));
      return data;
    },
    [supabase, router],
  );

  const signUp = useCallback(
    async (
      email: string,
      password: string,
      firstName: string,
      lastName: string,
      captchaToken?: string,
    ) => {
      const start = Date.now();
      trackEvent(AuthEvents.SIGN_UP_ATTEMPTED);

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { first_name: firstName, last_name: lastName },
          ...(captchaToken ? { captchaToken } : {}),
        },
      });
      if (error) {
        trackEvent(AuthEvents.SIGN_UP_FAILED, {
          error: error.message,
          duration_ms: Date.now() - start,
        });
        throw error;
      }
      trackEvent(AuthEvents.SIGN_UP_SUCCEEDED, { duration_ms: Date.now() - start });
      return data;
    },
    [supabase],
  );

  const verifyOtp = useCallback(
    async (email: string, token: string) => {
      const start = Date.now();
      trackEvent(AuthEvents.OTP_SUBMITTED);
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'signup',
      });
      if (error) {
        trackEvent(AuthEvents.OTP_FAILED, {
          error: error.message,
          duration_ms: Date.now() - start,
        });
        throw error;
      }
      trackEvent(AuthEvents.OTP_VERIFIED, { duration_ms: Date.now() - start });
      if (data.user) {
        identifyUser(data.user.id, {
          email: data.user.email,
          name:
            [data.user.user_metadata?.first_name, data.user.user_metadata?.last_name]
              .filter(Boolean)
              .join(' ') || undefined,
          created_at: data.user.created_at,
        });
        resetSessionExpiredGuard();
      }

      // Create user account on backend after email verification — this is critical.
      // Block the user until registration succeeds; all subsequent API calls depend on it.
      // Set flag BEFORE the Supabase call could fire onAuthStateChange to prevent
      // a concurrent /auth/sync from racing with /auth/register.
      if (data.session) {
        _registrationInProgress = true;
        const userMeta = data.user?.user_metadata;
        let registerResult: { ok: boolean; status: number };
        try {
          registerResult = await registerAccountWithBackend({
            first_name: userMeta?.first_name,
            last_name: userMeta?.last_name,
          });
        } finally {
          _registrationInProgress = false;
        }
        if (!registerResult.ok) {
          Sentry.captureMessage('backend_registration_failed_after_otp', {
            level: 'error',
            extra: { http_status: registerResult.status },
          });
          trackEvent(AuthEvents.SIGN_UP_FAILED, {
            error: 'backend_registration_failed',
            status: registerResult.status,
          });
          throw new Error(
            'Account setup failed. Please try signing in — your account may already be ready.',
          );
        }

        // Pre-fetch profile before navigating so it's already in the Zustand
        // store when the dashboard mounts — avoids a second round-trip from
        // the INITIAL_SESSION handler. Mirrors signInWithPassword() exactly.
        await fetchProfile(data.user?.id ?? 'unknown');
      }

      // ── Pending invitation flow ─────────────────────────────────────
      // If the user signed up from an invite link, the invite accept page
      // stored the token in a cookie. Auto-accept it now — mirroring the
      // auth callback page behavior. This handles the inline OTP path
      // where the auth callback is never visited.
      // Clear any stored post-auth redirect: the accepted-workspace URL is
      // the canonical destination; a stale redirect from another context
      // must not leak into this navigation.
      const acceptedWsId = await tryAutoAcceptInvite();
      if (acceptedWsId) {
        clearPostAuthRedirect();
        markAuthTransition();
        router.push(`/w/${acceptedWsId}/home`);
        return data;
      }

      // Navigate to workspace-scoped dashboard — pass server-authoritative
      // workspace ID to bypass stale stores/cookies. Respect a stored
      // post-auth redirect (e.g. signup from a share page) so deep-link
      // continuity works across the OTP flow.
      markAuthTransition();
      const postAuthRedirect = consumePostAuthRedirect();
      router.push(
        postAuthRedirect ??
          resolveDashboardUrl(useAuthProfileStore.getState().profile?.active_workspace_id),
      );
      return data;
    },
    [supabase, router],
  );

  const signInWithGoogle = useCallback(async () => {
    const { authOAuthActions } = await import('@/hooks/use-auth-oauth');
    await authOAuthActions.signInWithGoogle();
  }, []);

  const signInWithGitHub = useCallback(async () => {
    const { authOAuthActions } = await import('@/hooks/use-auth-oauth');
    await authOAuthActions.signInWithGitHub();
  }, []);

  const signInWithMicrosoft = useCallback(async () => {
    const { authOAuthActions } = await import('@/hooks/use-auth-oauth');
    await authOAuthActions.signInWithMicrosoft();
  }, []);

  const forgotPassword = useCallback(async (email: string, captchaToken?: string) => {
    trackEvent(AuthEvents.FORGOT_PASSWORD_SUBMITTED);
    // Use implicit flow for password reset emails. PKCE stores a
    // code_verifier in cookies that must be present when the link is
    // clicked — this breaks on iOS in-app browsers, cross-device, and
    // cross-browser scenarios. Implicit flow embeds tokens in the URL
    // hash fragment so it works regardless of where the link is opened.
    const client = getImplicitFlowClient();
    const { error } = await client.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
      ...(captchaToken ? { captchaToken } : {}),
    });
    if (error) throw error;
  }, []);

  const resetPassword = useCallback(
    async (newPassword: string) => {
      const start = Date.now();
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      if (error) {
        trackEvent(AuthEvents.PASSWORD_RESET_FAILED, {
          error: error.message,
          duration_ms: Date.now() - start,
        });
        throw error;
      }
      trackEvent(AuthEvents.PASSWORD_RESET_SUCCEEDED, { duration_ms: Date.now() - start });
      router.push('/login');
    },
    [supabase, router],
  );

  const resendVerification = useCallback(
    async (email: string, captchaToken?: string) => {
      trackEvent(AuthEvents.VERIFICATION_RESENT);
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: captchaToken ? { captchaToken } : undefined,
      });
      if (error) throw error;
    },
    [supabase],
  );

  const signOut = useCallback(async () => {
    // Notify backend to revoke the current session.
    // Uses the cookie-based proxy (consistent with all other API calls).
    // The backend extracts session_id from the JWT sid claim automatically.
    try {
      await fetch('/api/v1/auth/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...buildTracingHeaders(generateTraceContext()),
        },
        credentials: 'include',
      });
    } catch {
      // Best effort — session will expire server-side via inactivity/absolute timeout
    }

    trackEvent(AuthEvents.SIGN_OUT);
    Sentry.setUser(null);
    resetAnalytics();
    clearPendingRequests();
    broadcastSignOut();
    closeAuthChannel();

    // Wipe all persisted Zustand stores so the next user on this browser
    // cannot see the previous user's chats, sessions, searches, etc.
    clearAllPersistedStores();

    // Clear TanStack Query in-memory cache — prevents stale data from
    // being served if another user signs in before gcTime expires.
    try {
      const { getQueryClient } = await import('@/components/providers/query-provider');
      getQueryClient().clear();
    } catch {
      // Non-fatal — cache will naturally expire via gcTime
    }

    await supabase.auth.signOut();
    router.push('/login');
  }, [supabase, router]);

  const getAccessToken = useCallback(async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.access_token ?? null;
  }, [supabase]);

  return useMemo(
    () => ({
      status: state.status,
      user: state.user,
      session: state.session,
      profile,
      isLoading: state.status === 'loading',
      isAuthenticated: state.status === 'authenticated',
      signInWithPassword,
      signUp,
      verifyOtp,
      signInWithGoogle,
      signInWithGitHub,
      signInWithMicrosoft,
      forgotPassword,
      resetPassword,
      resendVerification,
      signOut,
      getAccessToken,
    }),
    [
      state,
      profile,
      signInWithPassword,
      signUp,
      verifyOtp,
      signInWithGoogle,
      signInWithGitHub,
      signInWithMicrosoft,
      forgotPassword,
      resetPassword,
      resendVerification,
      signOut,
      getAccessToken,
    ],
  );
}
